import { Box, makeStyles } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { IndexRange } from 'react-virtualized';
import { useTranslation } from 'react-i18next';
import { getProvisionalOutboundStocksForClientConditionedStock } from 'services/apiOperations';
import { transformObjectId } from 'services/apiPlatformTransformer';
import { Period } from 'types/Period';
import Loading from 'components/Loading';
import { SortingCenter } from 'types';
import { ClientStockList } from './ClientStockList';
import { ConditionedStockByClientByMonth, Filter, FilterFormType } from './types';
import FilterForm from './components/FilterForm';
import { getDateFilterForScroll } from './utils';

const useStyles = makeStyles(theme => ({
  emptyList: {
    width: '200vw',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
}));

type ConditionedByClientProps = {
  latestModificationOfStock: Date | null;
  sortingCenter?: SortingCenter | null;
  currentPeriod: Period | null;
  filters: Filter;
  handleChangeFilters: (filters: Filter) => any;
  setCurrentPeriod: (period: Period) => any;
  setExportButtonDisabled: (isDisabled: boolean) => any;
};

const initData: any[] = [];

export const NUMBER_OF_MONTH_BY_BATCH = 5;
let scrollPeriod: Period | null = null;

const ConditionedByClient: React.FC<ConditionedByClientProps> = ({
  latestModificationOfStock,
  sortingCenter,
  currentPeriod,
  filters,
  handleChangeFilters,
  setCurrentPeriod,
  setExportButtonDisabled,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [dataList, setDataList] = useState<ConditionedStockByClientByMonth[]>(initData);
  const [isLoading, setIsLoading] = useState(true);
  const [hasNextPage, setHasNextPage] = useState(true);

  useEffect(() => {
    if (!latestModificationOfStock || !sortingCenter) return;

    const endDate = latestModificationOfStock;
    const sortingCenterInitializationDate = new Date(sortingCenter.initializationDate);

    handleChangeFilters({
      clientIds: [],
      materialIds: [],
      period: getDateFilterForScroll(
        { startDate: sortingCenterInitializationDate, endDate },
        latestModificationOfStock,
        sortingCenterInitializationDate,
      ),
    });
  }, [latestModificationOfStock, sortingCenter, handleChangeFilters]);

  useEffect(() => {
    const loadFreshList = async () => {
      if (!filters?.period?.startDate || !filters?.period?.endDate || !sortingCenter) return;

      const provisionalOutboundStocks = await getProvisionalOutboundStocksForClientConditionedStock(
        transformObjectId(sortingCenter),
        filters.period.startDate,
        filters.period.endDate,
        filters.clientIds,
        filters.materialIds,
      );

      setIsLoading(false);

      if (provisionalOutboundStocks.length > 0) {
        setDataList(provisionalOutboundStocks);
        scrollPeriod = {
          startDate: filters.period.startDate,
          endDate: filters.period.endDate,
        };
        setHasNextPage(true);
      } else {
        setHasNextPage(false);
      }
    };

    loadFreshList();
  }, [filters.period.startDate, filters.period.endDate, filters.clientIds, filters.materialIds, sortingCenter]);

  const shouldDisable =
    (!isLoading && dataList?.length === 0) || !currentPeriod?.startDate || !currentPeriod?.endDate || !sortingCenter;
  useEffect(() => {
    setExportButtonDisabled(shouldDisable);
  }, [shouldDisable, setExportButtonDisabled]);

  if (!sortingCenter) return <Loading />;

  const handleSubmitFilterForm = async (filtersForm: FilterFormType) => {
    setIsLoading(true);
    setDataList([]);
    if (!sortingCenter?.initializationDate) return;

    handleChangeFilters({
      period: getDateFilterForScroll(
        currentPeriod,
        latestModificationOfStock,
        new Date(sortingCenter.initializationDate),
      ),
      materialIds: [filtersForm.materialId],
      clientIds: [filtersForm.clientId],
    });
  };

  return (
    <>
      <FilterForm
        filters={filters}
        onChangePeriod={setCurrentPeriod}
        onSubmit={handleSubmitFilterForm}
        defaultPeriod={currentPeriod}
        minStartDate={new Date(sortingCenter?.initializationDate)}
        maxEndDate={new Date()}
      />
      <Box display="flex" justifyContent="center">
        {dataList.length > 0 && (
          <ClientStockList
            hasNextPage={hasNextPage}
            isNextPageLoading={isLoading}
            list={dataList}
            loadNextPage={async (params: IndexRange) => {
              setIsLoading(true);

              let startDate: Date | null = null;
              let endDate: Date | null = null;

              if (scrollPeriod?.endDate && scrollPeriod?.startDate) {
                startDate = new Date(scrollPeriod.startDate);
                startDate.setMonth(scrollPeriod.startDate.getMonth() - NUMBER_OF_MONTH_BY_BATCH);
                endDate = scrollPeriod.startDate;
                endDate.setMonth(scrollPeriod.startDate.getMonth() - 1); // To avoid duplicates

                if (
                  (currentPeriod?.startDate && startDate < currentPeriod?.startDate) ||
                  startDate < new Date(sortingCenter.initializationDate)
                ) {
                  setHasNextPage(false);
                  setIsLoading(false);
                  return;
                }

                return getProvisionalOutboundStocksForClientConditionedStock(
                  transformObjectId(sortingCenter),
                  startDate,
                  endDate,
                  filters.clientIds,
                  filters.materialIds,
                ).then(provisionalOutboundStocks => {
                  setIsLoading(false);

                  if (scrollPeriod?.startDate) {
                    scrollPeriod.startDate = startDate;
                  }

                  if (provisionalOutboundStocks.length > 0) {
                    setDataList(oldValues => [...oldValues, ...provisionalOutboundStocks]);
                  } else {
                    setHasNextPage(false);
                  }
                });
              } else {
                setIsLoading(false);
              }
            }}
          />
        )}
        {!isLoading && dataList.length === 0 && (
          <div className={classes.emptyList}>
            <div>{t('conditionedStockByClientPage.message.noResult')}</div>
          </div>
        )}
      </Box>
    </>
  );
};
export default ConditionedByClient;
